import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControlLabel,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../context/AuthContext';
import server from '../global/server';
import useHandleErrors from '../hooks/useHandleErrors';

/**
 * Novo aceite das políticas por quem já tinha conta.
 *
 * Cinco documentos foram substituídos em 20/08/2026. Quem criou conta antes
 * disso aceitou textos que não existem mais, e nem havia registro de qual
 * versão — ele passou a existir na mesma data e só valia para cadastro novo.
 *
 * A tela não se fecha sozinha: aceitar ou sair são as duas saídas. É o que dá
 * sentido ao registro — um aviso que a pessoa pudesse dispensar provaria que
 * ela viu, não que concordou. Sair continua disponível justamente para que
 * concordar seja escolha, e não a única forma de recuperar a conta.
 *
 * A mesma tela serve a qualquer substituição futura: trocada a versão no
 * servidor, o documento volta a aparecer como pendente.
 */
const NovoAceiteDePoliticas = () => {
    const { LogOut } = useAuth();

    const [pendentes, setPendentes] = useState([]);
    const [totalVigentes, setTotalVigentes] = useState(0);
    const [concordou, setConcordou] = useState(false);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState();
    useHandleErrors(erro);

    const carregar = useCallback(async () => {
        try {
            const { data } = await server.get('/politicas/pendentes');
            setPendentes(data.data?.pendentes || []);
            setTotalVigentes(data.data?.total_vigentes || 0);
        } catch (falha) {
            // Sem resposta, nada aparece. Uma falha de rede não pode trancar a
            // pessoa fora da própria conta atrás de um aviso que nem carregou.
            setPendentes([]);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const aceitar = async () => {
        if (!concordou || enviando) return;

        try {
            setEnviando(true);
            await server.post('/politicas/aceitar');
            setPendentes([]);
        } catch (falha) {
            setErro(falha);
            setEnviando(false);
        }
    };

    if (carregando || pendentes.length === 0) return null;

    // Conta antiga é a que não aceitou nada: para ela mudou tudo. Quem tem um
    // documento pendente no meio dos outros precisa ouvir outra frase.
    const tudoPendente = pendentes.length === totalVigentes;

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown
            PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <GavelIcon sx={{ color: '#006a61' }} />
                        <Typography variant="h6" fontWeight={700}>
                            {tudoPendente
                                ? 'Nossos documentos mudaram'
                                : pendentes.length === 1
                                  ? 'Um documento foi atualizado'
                                  : 'Alguns documentos foram atualizados'}
                        </Typography>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {tudoPendente
                            ? 'Publicamos novas versões dos documentos que regem o uso da Empiria. Como você já tinha conta quando isso aconteceu, precisamos do seu aceite nas versões atuais para continuar.'
                            : 'Publicamos uma nova versão do que está listado abaixo. Precisamos do seu aceite para continuar.'}
                    </Typography>

                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        Vale a pena ler antes de aceitar. Cada link abre em uma aba nova, e esta tela
                        continua aqui esperando.
                    </Alert>

                    <Box
                        sx={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '12px',
                            p: 2,
                            bgcolor: '#fafafa',
                        }}>
                        <Stack spacing={1.25}>
                            {pendentes.map(documento => (
                                <Link
                                    key={documento.documento}
                                    href={documento.caminho}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        color: '#006a61',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                    }}>
                                    {documento.titulo}
                                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                                </Link>
                            ))}
                        </Stack>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={concordou}
                                onChange={evento => setConcordou(evento.target.checked)}
                                sx={{ color: '#006a61', '&.Mui-checked': { color: '#006a61' } }}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Li e concordo com {pendentes.length === 1 ? 'o documento' : 'os documentos'} acima.
                            </Typography>
                        }
                    />

                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
                        <Button
                            fullWidth
                            variant="text"
                            onClick={LogOut}
                            disabled={enviando}
                            sx={{ textTransform: 'none', color: '#666', height: 44 }}>
                            Sair da conta
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={aceitar}
                            disabled={!concordou || enviando}
                            sx={{
                                bgcolor: '#006a61',
                                borderRadius: '8px',
                                height: 44,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#005a52' },
                            }}>
                            {enviando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Aceitar e continuar'}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default NovoAceiteDePoliticas;
